import { gql } from "@apollo/client";
import { PlanType, RenewalOptions } from "../models";

export const GET_TRANSACTION_STATISTICS = gql`
  query GetEmployerStatistics($employerId: UUID!) {
    employerStatistics(employerId: $employerId) {
      employeeSpend {
        totalSpend
        totalTransactions
        monthlyStats {
          month
          year
          totalAmount
          transactionCount
        }
        currentMonthDailyStats {
          date
          totalAmount
          transactionCount
        }
      }
    }
  }
`;

export const CREATE_REWARD_SENDING_PLAN = gql`
  mutation createRewardPlan(
    $employerId: UUID!
    $request: CreateRewardSendingPlanRequestInput!
  ) {
    createRewardSendingPlan(employerId: $employerId, request: $request)
  }
`;

export const UPDATE_REWARD_SENDING_PLAN = gql`
  mutation updateRewardSendingPlan(
    $id: UUID!
    $employerId: UUID!
    $request: UpdateRewardSendingPlanRequestInput!
  ) {
    updateRewardSendingPlan(id: $id, employerId: $employerId, request: $request)
  }
`;

export const CREATE_PUBLIC_TRANSPORT_PLAN = gql`
  mutation createPublicTransportPlan(
    $employerId: UUID!
    $request: CreatePublicTransportPlanRequestInput!
  ) {
    createPublicTransportPlan(employerId: $employerId, request: $request)
  }
`;

export const UPDATE_PUBLIC_TRANSPORT_PLAN = gql`
  mutation updatePublicTransportPlan(
    $id: UUID!
    $employerId: UUID!
    $request: UpdatePublicTransportPlanRequestInput!
  ) {
    updatePublicTransportPlan(
      id: $id
      employerId: $employerId
      request: $request
    )
  }
`;

export const CREATE_GIFTING_PLAN = gql`
  mutation createGiftingPlan(
    $employerId: UUID!
    $request: CreateGiftingPlanRequestInput!
  ) {
    createGiftingPlan(employerId: $employerId, request: $request)
  }
`;

export const UPDATE_GIFTING_PLAN = gql`
  mutation updateGiftingPlan(
    $id: UUID!
    $employerId: UUID!
    $request: UpdateGiftingPlanRequestInput!
  ) {
    updateGiftingPlan(id: $id, employerId: $employerId, request: $request)
  }
`;

export const CREATE_ALLOWANCE_PLAN = gql`
  mutation createAllowancePlan(
    $employerId: UUID!
    $request: CreateAllowancePlanRequestInput!
  ) {
    createAllowancePlan(employerId: $employerId, request: $request)
  }
`;

export const UPDATE_ALLOWANCE_PLAN = gql`
  mutation updateAllowancePlan(
    $id: UUID!
    $employerId: UUID!
    $request: UpdateAllowancePlanRequestInput!
  ) {
    updateAllowancePlan(id: $id, employerId: $employerId, request: $request)
  }
`;

export interface MonthlyStats {
  month: number;
  year: number;
  totalAmount: number;
  transactionCount: number;
}

export interface DailyStats {
  date: string;
  totalAmount: number;
  transactionCount: number;
}

export interface EmployeeSpend {
  totalSpend: number;
  totalTransactions: number;
  monthlyStats: MonthlyStats[];
  currentMonthDailyStats: DailyStats[];
}

export interface EmployerStatistics {
  employeeSpend: EmployeeSpend;
}

export interface EmployerStatisticsData {
  employerStatistics: EmployerStatistics;
}

export interface EmployerStatisticsVars {
  employerId: string;
}

export interface ServiceSectorReference {
  id: string;
  name: string;
}

export interface PurchaseControls {
  isEnabled: boolean;
}

export interface PaymentControls {
  isEnabled: boolean;
  allowedTargetPlans?: string[];
  reasonCategories?: string[];
}

export interface TierRequest {
  id?: string;
  name: string;
  amount: number;
}

export interface RewardSendingControlsRequest {
  payments: PaymentControls;
}

interface PublicTransportPlanRequestInput {
  employerId: string;
  request: {
    name: string;
  };
}

export interface CreatePublicTransportPlanRequestInput
  extends PublicTransportPlanRequestInput {}

export interface UpdatePublicTransportPlanRequestInput
  extends PublicTransportPlanRequestInput {
  id: string;
}

interface GiftingPlanRequestInput {
  employerId: string;
  request: {
    name: string;
    tiers: TierRequest[];
  };
}

export interface CreateGiftingPlanRequestInput extends GiftingPlanRequestInput {
  request: {
    name: string;
    tiers: TierRequest[];
    renewalOptions: RenewalOptions;
  };
}

export interface UpdateGiftingPlanRequestInput extends GiftingPlanRequestInput {
  id: string;
}

interface RewardSendingPlanRequestInput {
  employerId: string;
  request: {
    name: string;
    tiers: TierRequest[];
    controls: RewardSendingControlsRequest;
  };
}

export interface CreateRewardSendingPlanRequestInput
  extends RewardSendingPlanRequestInput {
  request: {
    name: string;
    type: PlanType;
    tiers: TierRequest[];
    controls: RewardSendingControlsRequest;
    renewalOptions: RenewalOptions;
  };
}

export interface UpdateRewardSendingPlanRequestInput
  extends RewardSendingPlanRequestInput {
  id: string;
  request: {
    name: string;
    tiers: TierRequest[];
    controls: RewardSendingControlsRequest;
  };
}

interface AllowancePlanRequestInput {
  employerId: string;
  request: {
    name: string;
    tiers: TierRequest[];
    serviceSectorIds: string[];
  };
}

export interface CreateAllowancePlanRequestInput extends AllowancePlanRequestInput {
  request: {
    name: string;
    type: PlanType;
    tiers: TierRequest[];
    serviceSectorIds: string[];
    renewalOptions: RenewalOptions;
  };
}

export interface UpdateAllowancePlanRequestInput extends AllowancePlanRequestInput {
  id: string;
}